import { z } from 'zod'
import { ComponentCategory, ComponentStatus, InstallPosition } from '../enums'
import { specsSchema } from '../specs'
import { Id, Timestamp } from './common'
import { Usage, UsageAdjustment } from './ride'

export const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional()

export const Currency = z
  .string()
  .regex(/^[A-Z]{3}$/, 'Use a three-letter currency code, such as GBP.')

export const ComponentInstall = z.object({
  id: Id,
  component_id: Id,
  bike_id: Id,
  bike_name: z.string(),
  position: InstallPosition,
  installed_at: Timestamp,
  removed_at: Timestamp.nullable(),
})
export type ComponentInstall = z.infer<typeof ComponentInstall>

export const Component = z.object({
  id: Id,
  category: ComponentCategory,
  brand: z.string().nullable(),
  model: z.string().nullable(),
  /** Free text for spares and odds and ends. Where set, it is the part's name. */
  description: z.string().nullable(),
  part_number: z.string().nullable(),
  serial: z.string().nullable(),
  purchase_date: z.string().nullable(),
  /** Minor units (pence). */
  purchase_price_minor: z.number().int().nullable(),
  currency: z.string().nullable(),
  weight_g: z.number().int().nullable(),
  specs: z.record(z.string(), z.unknown()),
  status: ComponentStatus,
  notes: z.string().nullable(),
  /** The open install window, or null for a spare. */
  current_install: ComponentInstall.nullable(),
  /** Derived from rides in install windows plus adjustments. */
  usage: Usage,
  created_at: Timestamp,
  updated_at: Timestamp,
})
export type Component = z.infer<typeof Component>

export const ComponentList = z.object({ components: z.array(Component) })
export type ComponentList = z.infer<typeof ComponentList>

export const ComponentDetail = Component.extend({
  installs: z.array(ComponentInstall),
  adjustments: z.array(UsageAdjustment),
})
export type ComponentDetail = z.infer<typeof ComponentDetail>

const componentFields = {
  brand: optionalText(80),
  model: optionalText(120),
  description: optionalText(160),
  part_number: optionalText(80),
  serial: optionalText(80),
  purchase_date: z.iso.date().nullable().optional(),
  purchase_price_minor: z.number().int().min(0).max(100_000_000).nullable().optional(),
  currency: Currency.nullable().optional(),
  weight_g: z.number().int().min(0).max(100_000).nullable().optional(),
  specs: z.record(z.string(), z.unknown()).default({}),
  notes: optionalText(2000),
}

export function checkSpecs(
  category: ComponentCategory,
  specs: Record<string, unknown> | undefined,
  ctx: z.RefinementCtx,
) {
  if (!specs) return
  const result = specsSchema(category).safeParse(specs)
  if (!result.success) {
    for (const issue of result.error.issues) {
      ctx.addIssue({ ...issue, path: ['specs', ...issue.path] } as z.core.$ZodRawIssue)
    }
  }
}

export const CreateComponentBody = z
  .object({
    category: ComponentCategory,
    ...componentFields,
    /** Install straight onto a bike when creating from a build sheet. */
    install: z
      .object({
        bike_id: Id,
        position: InstallPosition.default('none'),
        installed_at: Timestamp.optional(),
      })
      .optional(),
  })
  .superRefine((b, ctx) => checkSpecs(b.category, b.specs, ctx))
export type CreateComponentBody = z.input<typeof CreateComponentBody>

/** Category cannot change: a fork does not become a chain. */
export const UpdateComponentBody = z
  .object({
    ...componentFields,
    specs: z.record(z.string(), z.unknown()).optional(),
    status: ComponentStatus.optional(),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, 'Nothing to update.')
export type UpdateComponentBody = z.input<typeof UpdateComponentBody>

export const InstallBody = z.object({
  bike_id: Id,
  position: InstallPosition.default('none'),
  /** Defaults to now. May be backdated. */
  installed_at: Timestamp.optional(),
})
export type InstallBody = z.input<typeof InstallBody>

export const RemoveBody = z.object({
  removed_at: Timestamp.optional(),
  /** What the part becomes once off the bike. Defaults to spare. */
  status: z.enum(['spare', 'worn_out', 'sold', 'binned']).default('spare'),
})
export type RemoveBody = z.input<typeof RemoveBody>

export const MoveBody = z.object({
  bike_id: Id,
  position: InstallPosition.default('none'),
  at: Timestamp.optional(),
})
export type MoveBody = z.input<typeof MoveBody>

export const ComponentListQuery = z.object({
  bike_id: Id.optional(),
  spare: z.enum(['true', 'false']).optional(),
})
